import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cloud, Lock, Watch } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Footer, ThemeToggle, Wordmark } from "@/components/Layout";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1639575003095-d55df63b97be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxhcHBsZSUyMHdhdGNoJTIwZGFyayUyMG1pbmltYWwlMjBwcm9kdWN0JTIwc3R1ZGlvfGVufDB8fHx8MTc4ODcxMDg3OHww&ixlib=rb-4.1.0&q=85";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut", delay },
});

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="flex min-h-screen flex-col">
      <header className="fixed left-0 right-0 top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Wordmark />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button asChild size="sm" data-testid="nav-open-dashboard-button">
                <Link to="/dashboard">Open dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" data-testid="nav-sign-in-button">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild size="sm" data-testid="nav-get-started-button">
                  <Link to="/signup">Get started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 pt-16">
        <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6 md:py-32 lg:px-8">
          <motion.p {...fade(0)} className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" data-testid="hero-eyebrow">
            Wearable-first memory infrastructure
          </motion.p>
          <motion.h1 {...fade(0.08)} className="mt-6 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            MIA captures. MIA remembers.
            <br />
            <span className="text-muted-foreground">ChatGPT thinks.</span>
          </motion.h1>
          <motion.p {...fade(0.16)} className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="hero-subheading">
            MIA is not another place to talk to AI. MIA is how your thoughts reach AI.
            Capture from your wrist, keep everything private, and ask ChatGPT about it later.
          </motion.p>
          <motion.div {...fade(0.24)} className="mt-10 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" data-testid="hero-create-account-button">
              <Link to="/signup">
                Create your MIA account <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="hero-sign-in-button">
              <Link to="/login">Sign in</Link>
            </Button>
          </motion.div>

          <motion.div {...fade(0.32)} className="mt-16 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-border bg-card px-6 py-5 font-mono text-xs text-muted-foreground sm:text-sm" data-testid="flow-diagram">
            <span>You</span>
            <ArrowRight className="h-3.5 w-3.5" />
            <span className="text-foreground">Apple Watch</span>
            <ArrowRight className="h-3.5 w-3.5" />
            <span className="text-foreground">MIA Cloud</span>
            <ArrowRight className="h-3.5 w-3.5" />
            <span className="text-foreground">ChatGPT</span>
          </motion.div>
        </section>

        <section className="border-t border-border/60">
          <div className="mx-auto grid max-w-5xl gap-12 px-4 py-12 sm:px-6 md:grid-cols-2 md:py-20 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-border">
              <img src={HERO_IMG} alt="Apple Watch on a dark, minimal background" className="h-full w-full object-cover" loading="lazy" data-testid="hero-watch-image" />
            </div>
            <div className="flex flex-col justify-center gap-10">
              <div className="flex gap-4">
                <Watch className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-display text-lg font-medium">Capture from your wrist</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Press a button, speak naturally, keep moving. No screens, no menus, no typing.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Cloud className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-display text-lg font-medium">MIA remembers</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Every capture is stored privately in your MIA memory layer. The original words are always preserved.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <Lock className="mt-1 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <h3 className="font-display text-lg font-medium">Private by design</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    Your memories are never public, never sold, never shown to anyone else. Export or delete everything, any time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border/60">
          <div className="mx-auto max-w-5xl px-4 py-12 text-center sm:px-6 md:py-20 lg:px-8">
            <h2 className="font-display text-2xl font-medium tracking-tight sm:text-3xl">Set up once. Then forget this website exists.</h2>
            <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
              Create your account, pair your Apple Watch, and connect ChatGPT when it launches. MIA works quietly in the background from then on.
            </p>
            <Button asChild size="lg" className="mt-8" data-testid="cta-create-account-button">
              <Link to="/signup">Create your MIA account</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
